import { PropsWithChildren, cloneElement, isValidElement, ReactElement, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { atom, useAtom } from 'jotai';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Modal, ModalContent } from '@restworld/ui-ds-base';

import { IEmployerFilterForm } from '../job-listing/employer-job-listing-filters';

import { cn } from '@restworld/utils-common';
export const toggleEmployerFiltersModalAtom = atom(false);
export const useToggleEmployerFiltersModal = () => {
  const [, setShowEmployerFiltersModal] = useAtom(toggleEmployerFiltersModalAtom);
  const closeEmployerFiltersModal = () => setShowEmployerFiltersModal(false);
  return {
    closeEmployerFiltersModal,
  };
};
export const EmployerFiltersModal = ({
  children,
  filterCount,
  formRef,
}: PropsWithChildren<{
  filterCount?: number;
  formRef?: React.RefObject<IEmployerFilterForm | null>;
}>) => {
  const { t } = useTranslation('common');
  const [showEmployerFiltersModal, setShowEmployerFiltersModal] = useAtom(toggleEmployerFiltersModalAtom);
  const open = () => setShowEmployerFiltersModal(true);
  const close = () => setShowEmployerFiltersModal(false);

  const isChildWithCloseProp = useMemo(() => checkChildWithCloseProp(children), [children]);

  return (
    <>
      <Modal open={showEmployerFiltersModal} onOpenChange={(v) => !v && close()}>
        <ModalContent
          variant="center"
          size="sm"
          title={t('filter')}
          footer={
            <ButtonsWrapper>
              <ButtonV2 className="flex-1" onClick={() => formRef.current?.applyFilters()}>
                {t('apply_filters')}
              </ButtonV2>
              <ButtonV2
                className="flex-1"
                variant="tertiary"
                onClick={() => formRef?.current?.removeFilters?.()}
                disabled={!filterCount}
              >
                {t('remove_filters')}
              </ButtonV2>
            </ButtonsWrapper>
          }
        >
          {isChildWithCloseProp ? cloneElement(children as ReactElement<any>, { close }) : children}
        </ModalContent>
      </Modal>
      <div className="w-fit pl-0 lg:pr-1 relative border-primary-800">
        <ButtonV2 onClick={open} variant="tertiary" StartIcon={AdjustmentsHorizontalIcon} indicator={filterCount}>
          {t('filter')}
        </ButtonV2>
      </div>
    </>
  );
};

export default EmployerFiltersModal;

interface ChildWithCloseProp {
  close: () => void;
}

const checkChildWithCloseProp = (child: unknown): child is ReactElement<any> & ChildWithCloseProp => {
  return isValidElement(child);
};

const ButtonsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-4 w-full', className)} {...props} />
);
